/*
 * Copyright (c) 2022 Certusoft, Inc. All Rights Reserved.
 *
 * This SOURCE CODE FILE, which has been provided by Certusoft as part
 * of a Certusoft product for use ONLY by licensed users of the product,
 * includes CONFIDENTIAL and PROPRIETARY information of Certusoft.
 *
 * USE OF THIS SOFTWARE IS GOVERNED BY THE TERMS AND CONDITIONS
 * OF THE LICENSE STATEMENT AND LIMITED WARRANTY FURNISHED WITH
 * THE PRODUCT.
 */

import { Injectable } from '@angular/core'
import * as _ from 'lodash'
import { HttpClient } from '@angular/common/http'

@Injectable({
    providedIn: 'root',
})
export class PermissionService {
    constructor(private $http: HttpClient) {}

    private authorizableActionCache: any
    private permissionCache: any
    private cachedUrls: any

    private handleFetchPermissionsSuccess(response: any) {
        let permissions = response.permissions
        let namespace = response.permissionNamespace
        this.cacheResponse(response)
        this.cachePermissions(permissions, namespace)
        return permissions
    }

    private handleFetchPermissionsError(response: any) {
        // do nothing for now
    }

    private cachePermission(permission: any, namespace: string) {
        let actionName = namespace + '.' + permission.actionName
        this.authorizableActionCache[actionName] = permission
    }

    private cachePermissions(permissions: any, namespace: string) {
        let vm = this
        let cacheablePermissions = _.filter(permissions, function (permission) {
            return permission.cacheable
        })
        _.each(cacheablePermissions, function (permission) {
            vm.cachePermission(permission, namespace)
        })
    }

    private cacheResponse(response: any) {
        let url = response.config.url
        if (!this.isCached(url)) {
            this.cachedUrls.push(url)
        }
    }

    private isCached(url: string) {
        return _.includes(this.cachedUrls, url)
    }

    findByActionName(namespacedActionName: string) {
        let authorizableAction = this.authorizableActionCache[namespacedActionName]
        return authorizableAction
    }
    fetchPermissions(permissionsPath: string) {
        let promise

        if ('true' === sessionStorage.getItem('tdw.auth.authed') && !this.isCached(permissionsPath)) {
            promise = this.$http
                .get(permissionsPath, {})
                .toPromise()
                .then(this.handleFetchPermissionsSuccess, this.handleFetchPermissionsError)
        } else {
            promise = new Promise(function (accept, reject) {
                accept([])
            })
        }

        return promise
    }
    isAuthorized(namespacedActionName: string) {
        let action = this.findByActionName(namespacedActionName)
        return action && action.visible && action.enabled
    }
    _getCache() {
        return this.authorizableActionCache
    }
    /**
     * Check to see if the permission string permission1 implies permission2.
     *
     * e.g., implies("foo:bar:*", "foo:bar:baz") is true
     * e.g., implies("foo:bar:*", "foo:quux:baz") is false
     *
     * @param permission1
     * @param permission2
     * @returns {boolean}
     */
    implies(permission1: string, permission2: string) {
        let parts = permission1.split(':')
        let otherParts = permission2.split(':')

        let partCount = parts.length
        let otherPartCount = otherParts.length
        let i = 0
        for (; i < otherPartCount; i++) {
            // If this permission has less parts than the other permission, everything after the number of parts contained
            // in this permission is automatically implied, so return true
            if (partCount - 1 < i) {
                return true
            }
            let part = parts[i]
            let otherPart = otherParts[i]

            let partSet = part.split(',')
            let otherPartSet = otherPart.split(',')
            if (!_.includes(partSet, '*')) {
                let opCount = otherPartSet.length
                for (let j = 0; j < opCount; j++) {
                    let segment = otherPartSet[j]
                    if (!_.includes(partSet, segment)) {
                        return false
                    }
                }
            }
        }
        // If this permission has more parts than the other parts, only imply it if all of the other parts are wildcards
        for (; i < partCount; i = i + 1) {
            let part = parts[i]
            let partSet = part.split(',')
            if (!_.includes(partSet, '*')) {
                return false
            }
        }
        return true
    }

    clearCache() {
        this.cachedUrls = []
        this.authorizableActionCache = {}
        this.permissionCache = {}
    }
}
